// Everything this module and an app's binding import evaluates before the listener exists, so imports are relative and skip the package barrel (React, web-vitals). The listener must be a side effect of the binding's own module scope, as main.tsx's first import: an effect or a main.tsx body statement misses throws during module evaluation and the first render. ./early-import-wall.test.ts enforces the wall.
import type { LogBackend } from "../backends/interface";
import type { CoreFieldsInput } from "../core-fields";
import { Logger } from "../logger";
import { setRedactedRoutes } from "../current-path";

// The error boundary is a source because React catches render errors, so they never reach `window`.
export type UnhandledErrorSource =
  | "window.onerror"
  | "unhandledrejection"
  | "react.error-boundary";

export type UnhandledErrorReporter = (
  error: Error,
  source: UnhandledErrorSource,
) => void;

// Bounds the pre-mount path so a boot that fails in a loop cannot become an unbounded POST loop against /api/log. Does not apply once LoggerProvider has adopted the boundary.
export const MAX_EARLY_EVENTS = 5;

// ErrorEvent.error and PromiseRejectionEvent.reason are untyped at runtime (a rejection can carry a string or nothing).
export function toError(value: unknown, fallbackMessage: string): Error {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  return new Error(fallbackMessage);
}

export interface EarlyErrorCaptureConfig {
  readonly redactedRoutes?: readonly string[];
  readonly coreFields: CoreFieldsInput;
  readonly getBackend: () => LogBackend;
  readonly getSessionId: () => string;
  readonly getVisitId: () => string;
}

export type EarlyErrorTarget = Pick<Window, "addEventListener">;

export interface EarlyErrorCapture {
  readonly setUnhandledErrorReporter: (
    next: UnhandledErrorReporter,
  ) => () => void;
  readonly reportUnhandledError: (
    error: Error,
    source: UnhandledErrorSource,
  ) => void;
  readonly install: (target: EarlyErrorTarget) => void;
}

export function createEarlyErrorCapture(
  config: EarlyErrorCaptureConfig,
): EarlyErrorCapture {
  setRedactedRoutes(config.redactedRoutes ?? []);
  let reporter: UnhandledErrorReporter | null = null;
  let earlyLogger: Logger | null = null;
  let earlyEventCount = 0;

  // A hand-off, not a second listener: LoggerProvider attaching its own handlers would report every post-mount failure twice.
  function setUnhandledErrorReporter(next: UnhandledErrorReporter): () => void {
    reporter = next;
    return () => {
      if (reporter === next) reporter = null;
    };
  }

  // A real Logger, not a hand-rolled fetch, so the event is the canonical toAxiomEvent() shape (a hand-built event missing a required field is dropped with a 204 and no trace). Lazy so a page that never fails builds nothing. It shares the page backend (and so the keepalive quota) but not LoggerProvider's Logger, which may sit behind the import wall.
  function createEarlyLogger(): Logger {
    return new Logger(
      config.getBackend(),
      {
        getSessionId: () => config.getSessionId(),
        getUserId: () => undefined,
        getVisitId: () => config.getVisitId(),
      },
      config.coreFields,
      false,
    );
  }

  // Nothing here may throw: a throwing boundary replaces the failure it reports with its own, recursively when called from window.onerror. randomUUID is absent outside secure contexts and a component-supplied reporter can throw.
  function reportUnhandledError(
    error: Error,
    source: UnhandledErrorSource,
  ): void {
    try {
      if (reporter !== null) {
        reporter(error, source);
        return;
      }
      if (earlyEventCount >= MAX_EARLY_EVENTS) return;
      earlyEventCount++;
      earlyLogger ??= createEarlyLogger();
      earlyLogger.error(
        "infra.error.unhandled",
        error,
        { source },
        { stack: true },
      );
    } catch {
      // Silent on purpose; the browser already logged the original failure.
    }
  }

  function install(target: EarlyErrorTarget): void {
    target.addEventListener("error", (event: ErrorEvent) => {
      reportUnhandledError(
        toError(event.error, event.message || "Unknown error"),
        "window.onerror",
      );
    });
    target.addEventListener("unhandledrejection", (event) => {
      reportUnhandledError(
        toError(event.reason, "Unhandled promise rejection"),
        "unhandledrejection",
      );
    });
  }

  return { setUnhandledErrorReporter, reportUnhandledError, install };
}
